import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable, InjectionToken } from '@angular/core';

import { map, Observable } from 'rxjs';

import { HALResponse } from '@valk-nx/core/lib/interfaces/hal.interface';
import { HotelInterface } from '@valk-nx/core/lib/interfaces/hotels.interface';

//Env variable
export const HOTEL_SERVICE_URL = new InjectionToken<string[]>(
	'hotel_service-url',
);

export const HOTEL_API_COOKIES = new InjectionToken<string[]>(
	'hotel_api-cookies',
);

@Injectable({ providedIn: 'root' })
export class HotelsService {
	private readonly http = inject(HttpClient);
	private readonly hotelUrl = inject(HOTEL_SERVICE_URL);
	private readonly apiCookies = inject<{
		withCredentials: boolean;
	}>(HOTEL_API_COOKIES);

	getHotels(language: string) {
		const headers = new HttpHeaders({
			'Accept-Language': language,
		});
		return this.http.get<HALResponse<HotelInterface[]>>(
			`${this.hotelUrl}hotels?language=${language}`,
			{ ...this.apiCookies, headers, observe: 'response' },
		);
	}

	// Note: Returns a list of hotels, which should always contain a single hotel
	getHotelsBySlug(language: string, slug: string, filterExtra?: string) {
		const headers = new HttpHeaders({
			'Accept-Language': language,
		});

		const filterExtraQuery = filterExtra ? `&extra=${filterExtra}` : '';

		return this.http.get<HALResponse<HotelInterface[]>>(
			`${this.hotelUrl}hotels?slug=${slug}&language=${language}${filterExtraQuery}`,
			{ ...this.apiCookies, headers, observe: 'response' },
		);
	}

	getHotelBySlug(language: string, hotelSlug: string) {
		return this.http.get<HALResponse<HotelInterface>>(
			`${this.hotelUrl}hotels?hotelSlug=${hotelSlug}&language=${language}`,
			{ ...this.apiCookies, observe: 'response' },
		);
	}

	getHotelByGuid(language: string, guid: string) {
		return this.http.get<HALResponse<HotelInterface>>(
			`${this.hotelUrl}hotels/${guid}?language=${language}`,
			{ ...this.apiCookies, observe: 'response' },
		);
	}

	// This data is hardcoded in the assets, based on an excel sheet in this ticket.
	// Script in the comment
	// https://aanzee.atlassian.net/browse/VPL-6846
	getRestaurantPhoto(
		hotelCode: string,
	): Observable<{ photo: string } | undefined> {
		const headers = new HttpHeaders();
		return this.http
			.get<Record<string, { photo: string }>>(
				`/assets/i18n/restaurant-details.json`,
				{
					headers,
					observe: 'response',
				},
			)
			.pipe(map(({ body }) => body?.[hotelCode]));
	}
}
